// src/push.js
// Web Push 구독/해제 유틸 (axios 인스턴스 사용)
// - Service Worker: /sw.js (public 루트)
// - API 엔드포인트: /api/notifications/push/public-key|subscribe|unsubscribe/
// - axios 인스턴스(api)는 X-CSRFToken + withCredentials 세팅이 되어 있어야 함.

import api from './api/axios';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
}

function isSecureContextOk() {
    // localhost 예외 외엔 https 필수
    return (
        window.isSecureContext ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
    );
}

function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone() {
    // iOS PWA 여부
    return window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
}

export async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        throw new Error('ServiceWorker 미지원 브라우저입니다.');
    }
    if (!isSecureContextOk()) {
        throw new Error('HTTPS 환경이 아니어서 푸시를 사용할 수 없습니다.');
    }
    // 캐시 갱신을 위해 쿼리스트링으로 버전 부여(선택)
    const reg = await navigator.serviceWorker.register('/sw.js?v=2', { scope: '/' });
    await navigator.serviceWorker.ready; // 모바일 레이스 컨디션 방지
    return reg;
}

export async function getCurrentSubscription() {
    const reg = await navigator.serviceWorker.getRegistration();
    return reg?.pushManager.getSubscription() || null;
}

// --- 서버에서 공개키 가져오기 ---
async function fetchServerPublicKey() {
    const { data } = await api.get('/api/notifications/push/public-key/');
    return data?.publicKey || '';
}

/**
 * enableWebPush
 * - publicKey 파라미터가 없으면 서버에서 자동으로 받아서 사용
 */
export async function enableWebPush({ publicKey } = {}) {
    try {
        const effectiveKey = publicKey || await fetchServerPublicKey();
        if (!effectiveKey) throw new Error('VAPID 공개키가 서버에서 비어 있습니다.');

        if (!('PushManager' in window)) throw new Error('이 브라우저는 Web Push를 지원하지 않습니다.');

        // iOS 사파리 안내: PWA(홈 화면에 추가) 상태에서만 푸시 수신
        if (isIOS() && !isStandalone()) {
            console.warn('iOS에서는 홈 화면에 추가(PWA) 후에만 푸시 수신이 가능합니다.');
        }

        const reg = await registerServiceWorker();

        // 권한 요청(사용자 제스처 이후 실행)
        let permission = Notification.permission;
        if (permission === 'default') permission = await Notification.requestPermission();
        if (permission !== 'granted') throw new Error('알림 권한이 허용되지 않았습니다.');

        // 이미 구독이 있으면 재사용(중복 생성 방지)
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(effectiveKey),
            });
        }

        // 서버에 구독 저장/업데이트(업서트)
        const json = sub.toJSON ? sub.toJSON() : null;
        await api.post('/api/notifications/push/subscribe/', {
            endpoint: sub.endpoint,
            keys: json?.keys, // { p256dh, auth }
        });

        return true;
    } catch (err) {
        const msg = err?.response?.data?.error || err?.message || '푸시 활성화 실패';
        throw new Error(msg);
    }
}

export async function disableWebPush() {
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (!sub) return;

        // 서버에서 구독 제거 (실패해도 클라이언트 구독은 해제)
        try {
            await api.post('/api/notifications/push/unsubscribe/', { endpoint: sub.endpoint });
        } catch (e) {
            console.warn('서버 구독 해제 실패(무시):', e?.response?.data || e?.message);
        }

        await sub.unsubscribe();
    } catch (err) {
        const msg = err?.response?.data?.error || err?.message || '푸시 해제 실패';
        throw new Error(msg);
    }
}
